import { useState } from 'react';
import { Button } from '../ui/button';

const COLS = 7;
const ROWS = 6;
const EMPTY = null;

const emptyBoard = () => Array.from({ length: ROWS }, () => Array(COLS).fill(EMPTY));

const drop = (board, col, player) => {
  for (let r = ROWS - 1; r >= 0; r--) {
    if (!board[r][col]) {
      const next = board.map((row) => [...row]);
      next[r][col] = player;
      return next;
    }
  }
  return null;
};

const winner = (board) => {
  const dirs = [[0, 1], [1, 0], [1, 1], [1, -1]];
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const cell = board[r][c];
      if (!cell) continue;
      for (const [dr, dc] of dirs) {
        let count = 0;
        for (let k = 0; k < 4; k++) {
          const rr = r + dr * k;
          const cc = c + dc * k;
          if (rr < 0 || cc < 0 || rr >= ROWS || cc >= COLS || board[rr][cc] !== cell) break;
          count++;
        }
        if (count === 4) return cell;
      }
    }
  }
  return board[0].every(Boolean) ? 'draw' : null;
};

const scoreBoard = (board) => {
  let score = 0;
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      if (board[r][c] === 'ai') score += c === 3 ? 3 : 1;
      if (board[r][c] === 'me') score -= c === 3 ? 3 : 1;
    }
  }
  return score;
};

const validCols = (board) => Array.from({ length: COLS }, (_, c) => c).filter((c) => !board[0][c]);

const search = (board, depth, isAi) => {
  const result = winner(board);
  if (result === 'ai') return { score: 10000 - depth };
  if (result === 'me') return { score: -10000 + depth };
  if (result === 'draw' || depth === 0) return { score: scoreBoard(board) };

  let bestCol = validCols(board)[0];
  let bestScore = isAi ? -Infinity : Infinity;
  for (const col of validCols(board)) {
    const next = drop(board, col, isAi ? 'ai' : 'me');
    const { score } = search(next, depth - 1, !isAi);
    if (isAi ? score > bestScore : score < bestScore) {
      bestScore = score;
      bestCol = col;
    }
  }
  return { score: bestScore, col: bestCol };
};

export function ConnectFour({ onScore }) {
  const [board, setBoard] = useState(emptyBoard);
  const [wins, setWins] = useState(0);
  const [status, setStatus] = useState('Your turn — you are gold');
  const [locked, setLocked] = useState(false);

  const play = (col) => {
    if (locked || board[0][col] || winner(board)) return;
    const afterMe = drop(board, col, 'me');
    setBoard(afterMe);
    const myResult = winner(afterMe);
    if (myResult) {
      if (myResult === 'me') {
        const next = wins + 1;
        setWins(next);
        onScore(next);
        setStatus('You beat the AI!');
      } else setStatus('Board full — draw.');
      return;
    }
    setLocked(true);
    setStatus('AI thinking...');
    setTimeout(() => {
      const { col: aiCol } = search(afterMe, 4, true);
      const afterAi = drop(afterMe, aiCol ?? validCols(afterMe)[0], 'ai');
      setBoard(afterAi);
      const aiResult = winner(afterAi);
      if (aiResult === 'ai') {
        setWins(0);
        setStatus('AI connected four. Streak reset.');
      } else if (aiResult === 'draw') {
        setStatus('Board full — draw.');
      } else {
        setStatus('Your turn');
      }
      setLocked(false);
    }, 220);
  };

  const reset = () => {
    setBoard(emptyBoard());
    setStatus('Your turn — you are gold');
    setLocked(false);
  };

  return (
    <div className="space-y-4" data-testid="game-connect-four">
      <div className="flex items-center justify-between text-sm">
        <span>Wins vs AI: <span className="text-amber-300 tabular-nums" data-testid="c4-wins">{wins}</span></span>
        <span className="text-xs text-muted-foreground">Connect four to win</span>
      </div>

      <div className="grid grid-cols-7 gap-1.5 p-2 rounded-lg border border-white/10 bg-black/50" data-testid="c4-board">
        {board.map((row, r) =>
          row.map((cell, c) => (
            <button
              key={`${r}-${c}`}
              type="button"
              onClick={() => play(c)}
              className="aspect-square rounded-full border border-white/10 bg-white/5 flex items-center justify-center hover:border-amber-400/40 transition-colors"
              data-testid={`c4-cell-${r}-${c}`}
            >
              {cell && (
                <span className={`block w-[80%] h-[80%] rounded-full ${cell === 'me' ? 'bg-amber-300' : 'bg-rose-500'}`} />
              )}
            </button>
          ))
        )}
      </div>

      <p className="text-center text-sm text-muted-foreground" data-testid="c4-status">{status}</p>
      <Button variant="outline" className="rounded-full w-full" onClick={reset} data-testid="c4-reset">New round</Button>
    </div>
  );
}
